import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Menu, message } from 'antd';
import { DownloadOutlined, CloseOutlined, WindowsOutlined, AppleOutlined } from '@ant-design/icons';
import './DownloadFloatingButton.css';
import versionService from '../services/versionService';

const DownloadFloatingButton: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [downloadOptions, setDownloadOptions] = useState<any[]>([]);
  const [currentVersion, setCurrentVersion] = useState('v1.0.0');

  useEffect(() => {
    // 检查是否已经永久关闭
    const permanentlyDismissed = localStorage.getItem('downloadButtonDismissed');
    if (permanentlyDismissed === 'true') {
      return;
    }

    // 检查是否是移动设备
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      return;
    }

    // 获取版本信息和下载选项
    const initializeDownloadInfo = async () => {
      try {
        const [version, options] = await Promise.all([
          versionService.getVersionNumber(),
          versionService.getAllDownloadOptions()
        ]);

        setCurrentVersion(version);
        setDownloadOptions(options);
      } catch (error) {
        console.error('获取下载信息失败:', error);
      }
    };

    initializeDownloadInfo();

    // 延迟3秒显示
    const timer = setTimeout(() => {
      setVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleDownload = async (platform?: 'windows' | 'mac' | 'linux', source?: 'github' | 'server') => {
    try {
      let downloadUrl = '';

      if (platform && source) {
        // 从下载选项中查找对应的URL
        const option = downloadOptions.find(opt =>
          opt.platform === platform && opt.source === source
        );
        downloadUrl = option?.url || '';
      } else {
        // 获取推荐的下载链接（根据用户操作系统）
        downloadUrl = await versionService.getRecommendedDownloadUrl();
      }

      if (!downloadUrl) {
        message.error('暂无可用的下载链接');
        return;
      }

      window.open(downloadUrl, '_blank');
      message.success(`正在下载 ${currentVersion} 桌面版应用...`);

      // 下载后暂时隐藏
      setVisible(false);
      setTimeout(() => setVisible(true), 30000); // 30秒后重新显示
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败，请稍后重试');
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  const handlePermanentDismiss = () => {
    localStorage.setItem('downloadButtonDismissed', 'true');
    setVisible(false);
    setDismissed(true);
  };

  const detectOS = () => {
    return versionService.detectOS();
  };

  const getOSInfo = () => {
    const os = detectOS();
    const osInfo = {
      windows: { name: 'Windows', icon: <WindowsOutlined /> },
      mac: { name: 'macOS', icon: <AppleOutlined /> },
      linux: { name: 'Linux', icon: <DownloadOutlined /> }
    };
    return osInfo[os];
  };

  const getMenuIcon = (platform: string) => {
    switch (platform) {
      case 'windows': return <WindowsOutlined />;
      case 'mac': return <AppleOutlined />;
      case 'linux': return <DownloadOutlined />;
      default: return <DownloadOutlined />;
    }
  };

  const downloadMenu = (
    <Menu
      items={[
        ...downloadOptions.map((option, index) => ({
          key: option.key,
          label: option.label,
          icon: getMenuIcon(option.platform),
          onClick: () => handleDownload(option.platform, option.source)
        })),
        ...(downloadOptions.length > 0 ? [{ type: 'divider' as const }] : []),
        {
          key: 'version-info',
          label: `当前版本: ${currentVersion}`,
          disabled: true
        },
        { type: 'divider' as const },
        {
          key: 'dismiss',
          label: '暂时隐藏',
          onClick: handleDismiss
        },
        {
          key: 'permanent',
          label: '不再显示',
          onClick: handlePermanentDismiss
        }
      ]}
    />
  );

  if (!visible || dismissed) {
    return null;
  }

  const osInfo = getOSInfo();

  return (
    <div className="download-floating-container">
      <div className="download-floating-card">
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          className="download-close-btn"
          onClick={handleDismiss}
        />

        <div className="download-content">
          <div className="download-icon">
            <img
              src="/icons/icon-192.png"
              alt="小酷笔记"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px'
              }}
            />
          </div>
          <div className="download-text">
            <div className="download-title">体验桌面版 {currentVersion}</div>
            <div className="download-desc">更流畅的使用体验</div>
          </div>
        </div>

        <div className="download-actions">
          <Button
            type="primary"
            size="small"
            icon={osInfo.icon}
            onClick={() => handleDownload(detectOS())}
            className="download-primary-btn"
          >
            {osInfo.name}
          </Button>

          <Dropdown overlay={downloadMenu} placement="topRight" trigger={['click']}>
            <Button size="small" type="text">
              更多
            </Button>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default DownloadFloatingButton;
